import type {
  CreateVendorPayload,
  UpdateVendorPayload,
  VendorContact,
  VendorContactListParams,
  VendorContactListResult,
  VendorDetail,
  VendorListParams,
  VendorListResponse,
} from "@page/VendorInfoPage/interface";
import type {
  EditSupplementDetail,
  SupplementDetail,
  SupplementListParams,
  SupplementListResult,
} from "@page/VendorSupplementPage/interface";
import downloadCSV from "@utils/downloadCSV";
import moment from "moment";
import { ApiResponse } from "@shared/types";
import CoreAPI from "./CoreAPI";
import { transformAPIKeyToCamel } from "./utils/transformAPIKeyToCamel";
import { transformCamelToSnake } from "./utils/transformCamelToSnake";

export enum ProfitsTitle {
  FINE,
  SLOT_PURCHASE,
  MARKETING_PROJECT,
  ACCOUNT_DISCOUNT,
}
export enum ProftisReviewStatus {
  Approve = 2,
  REJECT = 3,
}
export const convertProfitsTitle = (type: string) => {
  switch (type) {
    case "罰款":
      return ProfitsTitle.FINE;
    case "版位購買":
      return ProfitsTitle.SLOT_PURCHASE;
    case "行銷合作專案":
      return ProfitsTitle.MARKETING_PROJECT;
    case "帳款折抵":
      return ProfitsTitle.ACCOUNT_DISCOUNT;
    default:
      return -1;
  }
};
export interface CreateMarginAddTopPayload {
  title: string;
  amount: number;
  pdfDirPrefix?: string;
  pdfFilePath?: string;
}
export interface CreateTransferProfitsPayload {
  title: ProfitsTitle;
  amount: number;
}
export interface EditMarginAddTopPayload extends CreateMarginAddTopPayload {
  receiptsDate?: string;
}
export interface EditTransferProfitsPayload extends CreateTransferProfitsPayload {}
export interface ApproveTransferProfitsPayload {
  reviewStatus: ProftisReviewStatus;
}
export interface MarginBalanceListResponse {
  summaryList: {
    name: string;
    depositBalance: number;
  }[];
  balanceSum: number;
}
export interface MarginAddTopList {
  id: number;
  createdAt: string;
  title: string;
  amount: number;
  receiptsDate: string;
  attachment: string;
  receipts: string;
  staff: string;
}
export interface ProfitsConversionList {
  id: number;
  title: string;
  amount: number;
  invoiceNumber: string | null;
  invoicedAt: string | null;
  destroyedAt: string | null;
  vendor: number;
  reviewStatus: string;
  staff: string;
  createdDate: string;
  reviewDate: string | null;
}

export interface VenderSalesRecordItem {
  id: number;
  vendor: number;
  title: string;
  amount: number;
  startDt: string;
  endDt: string;
  remitDt: string;
  createdDt: string;
  attachment: string;
  staff: string;
  invoiceDt: string | null;
}
export interface AddSalesRecordItem {
  title: string;
  amount: number;
  startDt: string;
  endDt: string;
  remitDt: string;
  dirPrefix: string;
  filePath: string;
}

export interface SalesChannelList extends ApiResponse.ListResponseType {
  results: SalesChannelItem[];
}
export interface SalesChannelItem {
  id: number;
  name: string;
  group: {
    name: string;
    mode: number;
    note: string;
  };
  description: string;
  isActive: boolean;
  createdat: string;
}
const apiClient = new CoreAPI();

/**
 * ----- 廠商申請（API-2）-----
 */

export const vendorAPI = {
  fetchVendorList: async (params: VendorListParams): Promise<VendorListResponse> => {
    const req = await apiClient.getData("/manage/vendor/vendors/", transformCamelToSnake(params));
    return transformAPIKeyToCamel(req.data.result);
  },
  fetchVendorDetail: async (vendorId: number): Promise<VendorDetail> => {
    const req = await apiClient.getData(`/manage/vendor/vendors/${vendorId}/`, {});
    return transformAPIKeyToCamel(req.data.result);
  },
  fetchCreateVendor: async (payload: CreateVendorPayload): Promise<{ id: number }> => {
    const req = await apiClient.postData("/manage/vendor/vendors/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(req.data.result);
  },
  fetchUpdateVendor: async (id: number, payload: UpdateVendorPayload): Promise<VendorDetail> => {
    const response = await apiClient.patchData(`/manage/vendor/vendors/${id}/`, transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  reviewVendorApplication: async (
    id: number,
    payload: { isApproved: boolean; rejectReason?: string },
  ): Promise<{ applicationStatus: number }> => {
    const response = await apiClient.putData(`/manage/vendor/vendors/${id}/review/`, transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
};

/**
 * ----- 廠商申請(End)-----
 */

/**
 * 廠商合作關係管理
 */
export function fetchVendorManagement(params: { vendorId: number; isActive?: boolean }) {
  const { isActive, vendorId } = params;

  const req = apiClient.patchData(`/manage/vendor/vendors/${vendorId}/`, {
    is_active: isActive,
  });
  return req.then((res) => res.data.results);
}

/**
 * 取得廠商聯絡人列表
 */
export function fetchVendorContactList(params: VendorContactListParams): Promise<VendorContactListResult> {
  const { ownerName, vendorName, limit, offset, vendorId } = params;
  const req = apiClient.getData("/manage/vendor/vendor-contacts/", {
    name_q: ownerName,
    vendor_name_q: vendorName,
    limit,
    offset,
    vendor_id: vendorId,
  });
  return req.then((res) => transformAPIKeyToCamel(res.data.result));
}

/**
 * 批次修改商開負責人
 */
export function fetchOwnerUpdate(params: { vendorIds: number[]; ownerId: number }) {
  const { vendorIds, ownerId } = params;
  const req = apiClient.putData("/manage/vendor/vendors/set-owner/", {
    vendor_ids: vendorIds,
    owner_id: ownerId,
  });
  return req.then((res) => res.data);
}

/**
 * 取得廠商貨源列表
 */
export async function fetchVendorSource(vendorId: number) {
  const response = await apiClient.getData(`/manage/vendor/vendors/${vendorId}/vpc-modes/`, {});
  return transformAPIKeyToCamel(response.data.result);
}

/**
 * 下載廠商貨源
 */
const sourceClient = new CoreAPI();
export function fetchVendorSourceFile(vendorId: number, mode: number) {
  const req = sourceClient.getData(`/manage/vendor/vendors/${vendorId}/download-vpc-modes-report/`, {
    mode,
  });

  return req.then((res) => res);
}

/**
 * 修改廠商聯絡人
 */
export function fetchModifiedVendorContact(content: VendorContact, contactId: number) {
  const req = sourceClient.patchData(`/manage/vendor/vendor-contacts/${contactId}/`, {
    name: content.name,
    phone: content.phone,
    email: content.email,
    title: content.title,
    is_major: content.isMajor,
  });
  return req.then((res) => res);
}

/**
 * 新增廠商連絡人
 */
export function fetchNewVendorContact(content: VendorContact, vendorId: number): Promise<VendorContact> {
  const req = sourceClient.postData("/manage/vendor/vendor-contacts/", {
    name: content.name,
    phone: content.phone,
    email: content.email,
    vendor: vendorId,
  });
  return req.then((res) => {
    const { result } = res.data;
    return transformAPIKeyToCamel(result);
  });
}

export function fetchDeletedVendorContact(contactId: number) {
  const req = sourceClient.deleteData(`/manage/vendor/vendor-contacts/${contactId}/`, {});
  return req.then((res) => res.data);
}

// -------------- 補單 ---------------
/**
 * 取得補單列表
 */
export async function fetchSupplementList(filterParams: SupplementListParams): Promise<SupplementListResult> {
  const req = await apiClient.getData("/manage/vendor/supplements/", transformCamelToSnake(filterParams));
  return transformAPIKeyToCamel(req.data.result);
}

/**
 * 匯出補單
 */
export async function exportSupplement(filterParams: SupplementListParams) {
  const req = await apiClient.getData("/manage/vendor/supplements/csv-download/", transformCamelToSnake(filterParams));
  downloadCSV(req);
}

/**
 * 取得補單Detail
 */
export async function fetchSupplementDetail(id: number): Promise<SupplementDetail> {
  const req = await apiClient.getData(`/manage/vendor/supplements/${id}`, {});
  return transformAPIKeyToCamel(req.data.result);
}

/**
 * 新增補單
 */
export async function addSupplement(payload: Partial<EditSupplementDetail>) {
  const req = await apiClient.postData(
    "/manage/vendor/supplements/",
    transformCamelToSnake({
      ...payload,
      estimatedSupplementDate: moment(payload.estimatedSupplementDate).format("YYYY-MM"),
    }),
  );
  return transformAPIKeyToCamel(req.data);
}

/**
 * 修改補單
 */
export async function editSupplement(id: number, payload: Partial<EditSupplementDetail>) {
  const req = await apiClient.patchData(
    `/manage/vendor/supplements/${id}/`,
    transformCamelToSnake({
      ...payload,
      estimatedSupplementDate: moment(payload.estimatedSupplementDate).format("YYYY-MM"),
    }),
  );
  return transformAPIKeyToCamel(req.data);
}

/**
 * 審核補單
 */
export async function approveSupplement(id: number, payload: { isApproved: boolean }) {
  const req = await apiClient.putData(`/manage/vendor/supplements/${id}/approve/`, transformCamelToSnake(payload));
  return transformAPIKeyToCamel(req.data);
}

/**
 * 取得補單sku合作關係
 */
export async function fetchSupplementVenderRelation(params: {
  sku: string;
  vendor?: number;
}): Promise<{ sku: string; modes: number[]; salesChannel: { id: number; modes: number[] }[] }> {
  const req = await apiClient.getData("/manage/vendor/supplements/check-vpc", params);
  return transformAPIKeyToCamel(req.data.result);
}

/**
 * 作廢補單
 */
export async function deleteSupplement(id: number) {
  const req = await apiClient.deleteData(`/manage/vendor/supplements/${id}/`, {});
  return transformAPIKeyToCamel(req.data);
}

// 保證金餘額總表
export async function fetchMarginBalanceList(vendorId?: number): Promise<MarginBalanceListResponse> {
  const payload = {
    id: vendorId,
  };
  const response = await apiClient.getData("/manage/vendor/deposit-balance-summary/", payload);
  return transformAPIKeyToCamel(response.data.result);
}

// 保證金相關
export async function fetchMarginSendReceipt(vendorId: number, addTopId: number): Promise<string> {
  const response = await apiClient.postData(`/manage/vendor/vendors/${vendorId}/deposit/${addTopId}/receipts/`, {});
  return response.data.result;
}
export async function fetchDeleteMarginAddTop(vendorId: number, addTopId: number) {
  await apiClient.deleteData(`/manage/vendor/vendors/${vendorId}/deposit/${addTopId}/`, {});
}
export async function fetchMarginAddTopList(vendorId: number): Promise<MarginAddTopList[]> {
  const response = await apiClient.getData(`/manage/vendor/vendors/${vendorId}/deposit/`, {});
  return transformAPIKeyToCamel(response.data.result);
}
export async function fetchMarginAddTopInfo(vendorId: number, addTopId: number): Promise<MarginAddTopList> {
  const response = await apiClient.getData(`/manage/vendor/vendors/${vendorId}/deposit/${addTopId}/`, {});
  return transformAPIKeyToCamel(response.data.result);
}
export async function fetchCreateMarginAddTop(vendorId: number, payload: CreateMarginAddTopPayload) {
  await apiClient.postData(`/manage/vendor/vendors/${vendorId}/deposit/`, transformCamelToSnake(payload));
}
export async function fetchEditMarginAddTop(vendorId: number, addTopId: number, payload: EditMarginAddTopPayload) {
  await apiClient.putData(`/manage/vendor/vendors/${vendorId}/deposit/${addTopId}/`, transformCamelToSnake(payload));
}

// 利潤轉換
export async function fetchProfitsConversionList(vendorId: number): Promise<ProfitsConversionList[]> {
  const response = await apiClient.getData(`/manage/vendor/vendors/${vendorId}/transfer-profits/`, {});
  return transformAPIKeyToCamel(response.data.result);
}
export async function fetchProfitsConversionInfo(vendorId: number, profitsId: number): Promise<ProfitsConversionList> {
  const response = await apiClient.getData(`/manage/vendor/vendors/${vendorId}/transfer-profits/${profitsId}/`, {});
  return transformAPIKeyToCamel(response.data.result);
}
export async function fetchCreateTransferProfits(vendorId: number, payload: CreateTransferProfitsPayload) {
  await apiClient.postData(`/manage/vendor/vendors/${vendorId}/transfer-profits/`, transformCamelToSnake(payload));
}
export async function fetchEditTransferProfits(
  vendorId: number,
  profitsId: number,
  payload: EditTransferProfitsPayload,
) {
  await apiClient.putData(
    `/manage/vendor/vendors/${vendorId}/transfer-profits/${profitsId}/`,
    transformCamelToSnake(payload),
  );
}
export async function fetchDeleteTransferProfits(vendorId: number, profitsId: number) {
  await apiClient.deleteData(`/manage/vendor/vendors/${vendorId}/transfer-profits/${profitsId}/`, {});
}
export async function fetchApproveTransferProfits(
  vendorId: number,
  profitsId: number,
  payload: ApproveTransferProfitsPayload,
) {
  await apiClient.putData(
    `/manage/vendor/vendors/${vendorId}/transfer-profits/${profitsId}/approve/`,
    transformCamelToSnake(payload),
  );
}
export async function fetchMarginSendInvoice(vendorId: number, profitsId: number) {
  await apiClient.postData(`/manage/vendor/vendors/${vendorId}/transfer-profits/${profitsId}/invoice/`, {});
}

// 銷售紀錄
export async function fetchVenderSalesRecordList(vendorId: number): Promise<VenderSalesRecordItem[]> {
  const req = await apiClient.getData(`/manage/vendor/vendors/${vendorId}/profit-sales-records/`, {});
  return transformAPIKeyToCamel(req.data.result);
}
export async function addVenderSalesRecord(
  vendorId: number,
  addData: AddSalesRecordItem,
): Promise<VenderSalesRecordItem> {
  const params = transformCamelToSnake({
    ...addData,
    dirPrefix: `vendor/profit_sales_records/attachments/${vendorId}`,
  });
  const req = await apiClient.postData(`/manage/vendor/vendors/${vendorId}/profit-sales-records/`, params);
  return transformAPIKeyToCamel(req.data.result);
}
export async function deleteVenderSalesRecord(vendorId: number, recordId: number): Promise<VenderSalesRecordItem> {
  const req = await apiClient.deleteData(`/manage/vendor/vendors/${vendorId}/profit-sales-records/${recordId}`, {});
  return transformAPIKeyToCamel(req.data.result);
}
export async function createVendorSalesInvoice(vendorId: number, recordId: number) {
  const req = await apiClient.postData(
    `/manage/vendor/vendors/${vendorId}/profit-sales-records/${recordId}/invoice/`,
    {},
  );
  return transformAPIKeyToCamel(req.data.result);
}

//  取得通路列表
export async function fetchSalesChannelList(): Promise<SalesChannelList> {
  const response = await apiClient.getData("/manage/vendor/sales-channel/", {});
  return transformAPIKeyToCamel(response.data.result);
}
